import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CATALOG, findGame, type CatalogEntry } from '../games/catalog';
import { useLibrary } from '../lib/library';
import { sound } from '../lib/audio';
import { NavIcon } from './NavIcons';

/** A random game, never the one already open. */
function randomGame(currentPath: string): CatalogEntry {
  const pool = CATALOG.filter((g) => !currentPath.startsWith(g.path));
  return pool[Math.floor(Math.random() * pool.length)]!;
}

/** Dice that rolls you into a random game. */
function RandomButton() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [rolling, setRolling] = useState(false);
  const roll = () => {
    sound.unlock();
    setRolling(true);
    window.setTimeout(() => {
      setRolling(false);
      navigate(randomGame(pathname).path);
    }, 380);
  };
  return (
    <button type="button" className={`head-btn${rolling ? ' is-rolling' : ''}`} onClick={roll} title="Play a random game" aria-label="Play a random game">
      <NavIcon name="board" size={20} />
      <span className="head-btn__label">Random</span>
    </button>
  );
}

/** Heart that opens a quick list of your favorite games. */
function FavoritesButton() {
  const favorites = useLibrary()
    .favorites.map((id) => findGame(id))
    .filter((g): g is CatalogEntry => !!g);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div className="head-pop" ref={ref}>
      <button type="button" className={`head-btn${open ? ' is-on' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)} title="Your favorites" aria-label="Your favorites">
        <NavIcon name="heart" size={20} />
        {favorites.length > 0 && <span className="head-btn__count">{favorites.length}</span>}
      </button>
      {open && (
        <div className="head-pop__panel" role="dialog" aria-label="Your favorites">
          <p className="head-pop__title">Your favorites</p>
          {favorites.length ? (
            <ul className="head-pop__list">
              {favorites.map((g) => (
                <li key={g.id}>
                  <Link to={g.path}>
                    {g.thumb && <img src={g.thumb.replace('/covers/', '/covers-sm/')} alt="" width={64} height={48} loading="lazy" />}
                    <span>{g.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="head-pop__empty">Tap the heart under any game to save it here.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Quick buttons at the top right of every page. */
export function HeaderActions() {
  return (
    <>
      <RandomButton />
      <FavoritesButton />
    </>
  );
}
